/*jshint bitwise: false*/
(function () {
  'use strict';

  var id = 'bar-chart';
  angular.module('wsLrs.cards').constant('YAxis', Object.freeze({
    Score: {
      id: 'score',
      title: 'Score'
    },
    Duration: {
      id: 'duration',
      title: 'Time'
    },
    Default: {
      id: 'default',
      title: null
    }
  })).constant('BarChartTemplate', {
    chart: {
      alignTicks: false,
      plotBorderWidth: 1
    },
    title: {
      text: ''
    },
    xAxis: {
      categories: [],
      labels: {
        useHTML: true
      }
    },
    yAxis: [{
      id: 'default',
      title: null
    }],
    tooltip: {
      followPointer: true,
      style: {
        padding: '1px'
      },
      // WSLRS-5076 Default is 8px, caused strange bleed with legend so made margin 8px below instead
      footerFormat: '</table></div>',
      shared: true
    }
  }).factory(id + '.Builder', ['$filter', '$sanitize', '$location', '$q', 'AggregationUtils', 'CardQueryResource', 'ChartUtils', 'QueryES', 'wsColors', 'Utils', 'Logger', 'HighchartsInterface', 'MomentInterface', 'ReportLinks', 'YAxis', 'BarChartTemplate', function ($filter, $sanitize, $location, $q, AggUtils, CardQueryResource, ChartUtils, QueryES, wsColors, Utils, Logger, HighchartsInterface, MomentInterface, ReportLinks, YAxis, chartTemplate) {
    // background methods, etc.
    var assignMethods = Utils.assignMethods,
      cata = Utils.cata,
      clamp = Utils.clamp,
      fork = Utils.fork,
      getIn = Utils.getIn,
      setIn = Utils.setIn,
      updateIn = Utils.updateIn,
      wordWrap = Utils.wordWrap;

    // highcharts methods
    var getColorOption = HighchartsInterface.getColorOption;

    // initChart produces a fresh chart config, from chartTemplate
    var initChart = function initChart() {
      return JSON.parse(JSON.stringify(chartTemplate));
    };

    // set up bar-chart report environment
    // configureLabels updates the provided environment with some configuration derived from the report
    // configuration
    var configureLabels = function configureLabels(env) {
      var compactLabels = env.compactLabels || false;
      var maxLabelLength = compactLabels ? 10 : 50;
      if (env.config.maxLabelLength) {
        maxLabelLength = env.config.maxLabelLength;
      }
      return Object.assign({}, env, {
        compactLabels: compactLabels,
        maxLabelLength: maxLabelLength
      });
    };

    // initSeries produces a fresh series config object
    var initSeries = function initSeries() {
      return {
        borderColor: '#FFF',
        borderWidth: 2,
        events: {
          legendItemClick: function legendItemClick() {
            return true;
          }
        },
        data: []
      };
    };

    // isMeasure and isBenchmark determine whether a given series represents a measure or a benchmark
    var isMeasure = function isMeasure(d) {
      return !d.benchmark;
    };
    var isBenchmark = function isBenchmark(d) {
      return !!d.benchmark;
    };

    // setSeriesName derives the name for a series, which in our case is the name of the measure that produces
    // the data in each grouping.  in the case of a benchmark, the name will be the name of the associated
    // measure along with some decoration
    var setSeriesName = function setSeriesName() {
      return cata([isMeasure, function (d) {
        return updateIn(d, 'series.name', $sanitize(d.measure.name), {
          force: true
        });
      }], [isBenchmark, function (d) {
        return updateIn(d, 'series.name', "".concat($sanitize(d.measure.name), " (Target)"), {
          force: true
        });
      }]);
    };

    // setSeriesColor determines the color of the bar that will represent a 'series' in each grouping. for each
    // measure, we read from the report configuration's colors array, if it exists; otherwise we read from
    // the highcharts default color.  The color of a benchmark is determined by the color of its associated
    // measure
    var rgb = clamp(0, 255);
    var rgbValue = function rgbValue(r, g, b) {
      return rgb(r) << 16 | rgb(g) << 8 | rgb(b);
    };
    var rgbString = function rgbString(c) {
      var s = c.toString(16);
      var l = 6;
      var p = '0';
      var n = Math.max(0, l - s.length);
      return '#' + p.repeat(n) + s;
    };
    var augmentRGB = function augmentRGB(c, dc) {
      var i = parseInt(c.slice(1), 16);
      var r = (i >> 16) + dc;
      var g = (i >> 8 & 0x00ff) + dc;
      var b = (i & 0x0000ff) + dc;
      var hc = rgbValue(r, g, b);
      return rgbString(hc);
    };
    var setSeriesColor = function setSeriesColor(_ref) {
      var config = _ref.config,
        chartConfig = _ref.chartConfig;
      return cata([isMeasure, function (d) {
        return updateIn(d, 'series.color', ChartUtils.getSeriesColor(config, d.colorIndex, getColorOption(d.colorIndex), d.measure), {
          force: true
        });
      }], [isBenchmark, function (d) {
        return updateIn(d, 'series.color', augmentRGB(getIn(chartConfig.data, "[".concat(d.seriesIndex - 1, "].series.color"), {
          alt: '#7f7f7f'
        }), 48), {
          force: true
        });
      }]);
    };

    // addSeriesMouseObservers subscribes each series to the mouseOver and mouseOut events defined
    // in report-links.js
    var addSeriesMouseObservers = function addSeriesMouseObservers(_ref2) {
      var config = _ref2.config;
      return function (d) {
        ReportLinks.BarCharts.registerObservers(d.series, [ReportLinks.BarCharts.makeEventObserver('point', ['mouseOver', 'mouseOut'])], {
          config: config
        });
        return d;
      };
    };

    // buildChartConfig produces a chartConfig object that contains some info that will be used to configure
    // the y-axes (measure axes) in the chart (hasScore, hasDuration, and isPercentageBase) and most
    // importantly, contains a list, data, containing details about each measure and benchmark that will be
    // included in the chart
    var buildChartConfig = function buildChartConfig(env) {
      var isHidden = function isHidden(i) {
        return !!env.config.hiddenMeasures && !!env.config.hiddenMeasures.includes(i);
      };
      var hasBenchmark = function hasBenchmark(i) {
        return !!env.config.benchmarks && !!env.config.benchmarks['m' + i];
      };
      var getBenchmark = function getBenchmark(i) {
        return env.config.benchmarks['m' + i];
      };
      var mHasScore = function mHasScore(m) {
        return m.valueProducer.statementProperty === 'result.score.scaled';
      };
      var mHasDuration = function mHasDuration(m) {
        return AggUtils.isDurationBasedMeasure(m);
      };
      var mIsPercentageBased = function mIsPercentageBased(m) {
        return m.valueProducer.valueType === 'percentage';
      };
      var getMeasureAxis = cata([mHasScore, function () {
        return YAxis.Score.id;
      }], [mHasDuration, function () {
        return YAxis.Duration.id;
      }]);
      var updateAxisTypeInfo = function updateAxisTypeInfo(_ref3, _ref4) {
        var anyScore = _ref3.anyScore,
          anyDuration = _ref3.anyDuration,
          allPercentageBased = _ref3.allPercentageBased;
        var hasScore = _ref4.hasScore,
          hasDuration = _ref4.hasDuration,
          isPercentageBased = _ref4.isPercentageBased;
        return {
          anyScore: anyScore || hasScore,
          anyDuration: anyDuration || hasDuration,
          allPercentageBased: allPercentageBased && isPercentageBased
        };
      };
      var _env$config$measures$ = env.config.measures.reduce(function (_ref5, measure, i) {
          var seriesCount = _ref5.seriesCount,
            measureCount = _ref5.measureCount,
            data = _ref5.data,
            anyScore = _ref5.anyScore,
            anyDuration = _ref5.anyDuration,
            allPercentageBased = _ref5.allPercentageBased;
          var hasScore = mHasScore(measure);
          var hasDuration = mHasDuration(measure);
          var isPercentageBased = mIsPercentageBased(measure);
          var yAxis = getMeasureAxis(measure);
          var newAxisTypeInfo = updateAxisTypeInfo({
            anyScore: anyScore,
            anyDuration: anyDuration,
            allPercentageBased: allPercentageBased
          }, {
            hasScore: hasScore,
            hasDuration: hasDuration,
            isPercentageBased: isPercentageBased
          });
          var getSeriesDataConfig = function getSeriesDataConfig() {
            var spec = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
            return Object.assign({}, {
              measuresIndex: i,
              colorIndex: measureCount,
              seriesIndex: seriesCount,
              measure: measure,
              hasScore: hasScore,
              hasDuration: hasDuration,
              isPercentageBased: isPercentageBased,
              yAxis: yAxis,
              series: initSeries()
            }, spec);
          };
          if (isHidden(i)) {
            return {
              seriesCount: seriesCount,
              measureCount: measureCount,
              data: data,
              anyScore: anyScore,
              anyDuration: anyDuration,
              allPercentageBased: allPercentageBased
            };
          } else if (hasBenchmark(i)) {
            return Object.assign({
              seriesCount: seriesCount + 2,
              measureCount: measureCount + 1,
              data: data.concat([getSeriesDataConfig(), getSeriesDataConfig({
                seriesIndex: seriesCount + 1,
                benchmark: getBenchmark(i)
              })])
            }, newAxisTypeInfo);
          } else {
            return Object.assign({
              seriesCount: seriesCount + 1,
              measureCount: measureCount + 1,
              anyScore: newAxisTypeInfo.anyScore,
              anyDuration: newAxisTypeInfo.anyDuration,
              allPercentageBased: newAxisTypeInfo.allPercentageBased,
              data: data.concat([getSeriesDataConfig()])
            }, newAxisTypeInfo);
          }
        }, {
          seriesCount: 0,
          measureCount: 0,
          data: [],
          anyScore: false,
          anyDuration: false,
          allPercentageBased: true
        }),
        data = _env$config$measures$.data,
        anyScore = _env$config$measures$.anyScore,
        anyDuration = _env$config$measures$.anyDuration,
        allPercentageBased = _env$config$measures$.allPercentageBased;
      return {
        data: data,
        anyScore: anyScore,
        anyDuration: anyDuration,
        allPercentageBased: allPercentageBased
      };
    };

    // configureSeries adds some details to the configuration objects created by buildChartConfig for each
    // series in the chart.
    var configureSeries = function configureSeries(_ref6) {
      var config = _ref6.config,
        chartConfig = _ref6.chartConfig;
      var setName = setSeriesName();
      var setColor = setSeriesColor({
        config: config,
        chartConfig: chartConfig
      });
      var addMouseObservers = addSeriesMouseObservers({
        config: config
      });
      return chartConfig.data.map(setName).map(setColor).map(addMouseObservers);
    };

    // configureChart sets some details on the highcharts chart object, including the 'chart.series' array,
    // derived from the chartConfig
    var configureChart = function configureChart(env) {
      var config = env.config,
        maxLabelLength = env.maxLabelLength,
        chart = env.chart,
        chartConfig = env.chartConfig;
      setIn(chart, 'chart.type', config.vertical ? 'column' : 'bar', {
        force: true
      });
      setIn(chart, 'xAxis.labels.formatter', function () {
        return $filter('truncate')(this.value, maxLabelLength);
      }, {
        force: true
      });
      setIn(chart, 'yAxis.allowDecimals', config.displayAxisDecimals, {
        force: true
      });
      setIn(chart, 'tooltip.headerFormat', '<div class="watershed-tooltip"><table><thead><tr><th>{point.x}</th></tr></thead>', {
        force: true
      });
      setIn(chart, 'tooltip.pointFormat', '<tr style="color:{series.color}">{point.tooltipContents}</tr>', {
        force: true
      });
      setIn(chart, 'legend.labelFormatter', function () {
        return wordWrap(this.name + (config.stacking === 'percent' ? ' %' : ''), 40);
      }, {
        force: true
      });
      setIn(chart, 'series', chartConfig.data.map(function (d) {
        return d.series;
      }), {
        force: true
      });
      setIn(chart, "plotOptions.series", {}, {
        force: true
      });
      ReportLinks.BarCharts.registerObservers(chart, [ReportLinks.BarCharts.makeEventObserver('plotOptions.bar', ['click']), ReportLinks.BarCharts.makeEventObserver('plotOptions.column', ['click'])], {
        config: config
      });
      return chart;
    };

    // getPrimaryMeasureAxis gets the first measure axis from the highcharts chart object, or builds a new axis
    // config if necessary (i.e. if all measures are percentage-based)
    var getPrimaryMeasureAxis = function getPrimaryMeasureAxis(_ref7) {
      var config = _ref7.config,
        chartConfig = _ref7.chartConfig,
        chart = _ref7.chart;
      if (chartConfig.allPercentageBased) {
        return Object.assign({}, YAxis.Default, {
          allowDecimals: config.displayAxisDecimals,
          max: 1,
          tickInterval: 0.1,
          labels: {
            formatter: function formatter() {
              return this.value * 100 + '%';
            }
          }
        });
      }
      return chart.yAxis[0];
    };

    // getScoreMeasureAxis builds a new axis config if needed (i.e. when some measure is score-based)
    var getScoreMeasureAxis = function getScoreMeasureAxis(_ref8) {
      var chartConfig = _ref8.chartConfig;
      if (chartConfig.anyScore) {
        return Object.assign({}, YAxis.Score, {
          allowDecimals: false,
          opposite: true,
          max: 1,
          tickInterval: 0.1,
          labels: {
            formatter: function formatter() {
              return this.value * 100 + '%';
            }
          }
        });
      }
    };

    // getDurationMeasureAxis builds a new axis config if needed (i.e. when some measure is duration-based)
    var getDurationMeasureAxis = function getDurationMeasureAxis(_ref9) {
      var chartConfig = _ref9.chartConfig;
      if (chartConfig.anyDuration) {
        return Object.assign({}, YAxis.Duration, {
          allowDecimals: false,
          opposite: true,
          labels: {
            formatter: function formatter() {
              return $filter('compactTime')(this.value);
            }
          }
        });
      }
    };

    // buildMeasureAxes sets up the measure axes (y axes) for the chart
    var buildMeasureAxes = function buildMeasureAxes(_ref0) {
      var config = _ref0.config,
        chartConfig = _ref0.chartConfig,
        chart = _ref0.chart;
      // highcharts lets us use as many yAxes as we want!
      // each series tells us which yAxis it's scaled on using the property "yAxis"
      chartConfig.data.forEach(function (d) {
        return chart.series[d.seriesIndex].yAxis = d.yAxis;
      });
      // We will set score % based measure on the 2nd yAxis (id = 'score'),
      // we place duration based measure on 3rd axis (id = 'duration')
      // - if all the visible measures are percentage-based, make the primary percentage-based
      // - add a new yAxis for score (index = 1)
      // - add a new yAxis for duration (index = 2, or 1 if there is no score axis)
      chart.yAxis = [getPrimaryMeasureAxis({
        config: config,
        chartConfig: chartConfig,
        chart: chart
      }), getScoreMeasureAxis({
        config: config,
        chartConfig: chartConfig,
        chart: chart
      }), getDurationMeasureAxis({
        config: config,
        chartConfig: chartConfig,
        chart: chart
      })].filter(function (x) {
        return x !== undefined;
      });
      // hide y-axis labels, if there are more than 2
      if (chart.yAxis.length > 2) {
        chart.yAxis.forEach(function (a) {
          a.labels = {
            enabled: false
          };
          a.title = null;
        });
      }
    };
    var validData = function validData(_ref1) {
      var values = _ref1.values;
      return Array.isArray(values) && typeof values[0].value !== 'undefined';
    };

    // buildDimensionAxis populates the
    var buildDimensionAxis = function buildDimensionAxis(_ref10, _ref11) {
      var config = _ref10.config,
        compactLabels = _ref10.compactLabels,
        chart = _ref10.chart;
      var values = _ref11.values;
      return chart.xAxis.categories.push(AggUtils.getDimensionDisplay(values[0], config.dimensions[0], compactLabels));
    };
    var formatPercentageLabel = function formatPercentageLabel(_ref12, aggregations, value, label) {
      var config = _ref12.config,
        chartConfig = _ref12.chartConfig;
      // todo: get aggreagations and measures from chartConfig.data
      if (config.stacking === 'percent') {
        var total = chartConfig.data.map(function (d) {
          return aggregations[d.measuresIndex];
        }).reduce(function (acc, curr) {
          return acc + parseFloat(curr.value);
        }, 0); // todo: add aggregations in buildChartConfig
        var percentage = AggUtils.getMeasureValueDisplay({
          value: value / total
        }, {
          valueProducer: {
            valueType: 'percentage',
            type: 'percentage'
          }
        });
        label = "".concat(label, " (").concat(percentage, ")");
      }
      return label;
    };
    var getMeasureTargetLabel = function getMeasureTargetLabel(measure, measureConfig) {
      var m = Object.assign({}, measure, {
        value: measure.target
      });
      return AggUtils.getMeasureValueDisplay(m, measureConfig);
    };

    // addSeriesData adds the appropriate measure and dimension values to the configuration for each bar,
    // representing a series in given grouping (i.e. this will be applied to each measure for each dimension
    // value
    var addSeriesData = function addSeriesData(_ref13) {
      var aggregations = _ref13.aggregations,
        values = _ref13.values;
      return function (d) {
        return Object.assign({}, d, {
          result: aggregations[d.measuresIndex],
          dimension: values[0].value
        });
      };
    };

    // labelSeriesData like addSeriesData will label each series (bar) in each grouping (dimension value)
    var labelSeriesData = function labelSeriesData(_ref14) {
      var config = _ref14.config,
        chartConfig = _ref14.chartConfig,
        aggregations = _ref14.aggregations;
      return function (d) {
        if (typeof d.result === 'undefined') {
          return;
        }
        d.label = isBenchmark(d) ? getMeasureTargetLabel(d.result, d.measure) : AggUtils.getMeasureValueDisplay(d.result, d.measure);

        // If using a percent stack, add the percentage to the label
        d.label = formatPercentageLabel({
          config: config,
          chartConfig: chartConfig
        }, aggregations, d.result.value, d.label);
        return d;
      };
    };
    var formatMeasureValue = cata([function (_ref15) {
      var measureValue = _ref15.measureValue,
        measureConfig = _ref15.measureConfig;
      return measureConfig.valueProducer.type === "TIME_BETWEEN";
    }, function (_ref16) {
      var measureValue = _ref16.measureValue;
      return measureValue / 10;
    }], [function (_ref17) {
      var measureValue = _ref17.measureValue,
        measureConfig = _ref17.measureConfig;
      return measureConfig.aggregation.type === "GROUP_PERCENT";
    }, function (_ref18) {
      var measureValue = _ref18.measureValue;
      return measureValue * 100;
    }], [function (_ref19) {
      var measureValue = _ref19.measureValue;
      return _.isString(measureValue);
    }, function (_ref20) {
      var measureValue = _ref20.measureValue;
      return parseFloat(measureValue);
    }], [function () {
      return true;
    }, function (_ref21) {
      var measureValue = _ref21.measureValue;
      return measureValue;
    }]);
    var formatSeriesValue = function formatSeriesValue(d) {
      if (d.result.value) {
        d.result.displayValue = formatMeasureValue({
          measureValue: d.result.value,
          measureConfig: d.measure
        });
      }
      if (d.result.target) {
        d.result.displayTarget = formatMeasureValue({
          measureValue: d.result.target,
          measureConfig: d.measure
        });
      }
      return d;
    };
    var configureLinkParams = function configureLinkParams(d) {
      d.linkParams = ReportLinks.BarCharts.LinkParameters.initialize(d.result, d.seriesIndex, d.dimension);
      d.linkParams = ReportLinks.BarCharts.LinkParameters.finalize(d.linkParams, d.result.displayValue, d.label);
      return d;
    };
    var getSeriesYValue = function getSeriesYValue(d) {
      return isBenchmark(d) ? _.isNumber(d.result.displayTarget) ? d.result.displayTarget : _.isNumber(d.result.target) ? d.result.target : null : _.isNumber(d.result.displayValue) ? d.result.displayValue : _.isNumber(d.result.value) ? d.result.value : null;
    };
    var getSeriesName = function getSeriesName(d) {
      return _.isNull(d.label) ? '--' : d.label;
    };

    // note: in order to differentiate benchmark bars by size, rather than color, we will have to
    //  explicitly set the size of measure bars and derive the benchmark size from that, or
    //  we'll have to predefine benchmark sizes for various views and page sizes.
    // note: for reference, we originally discussed differentiating benchmarks by size, rather than color,
    //  leaving this here in case this requirement comes back
    // const setPointSize = (chart, d, n) => {
    //     if (isBenchmark(d)) {
    //         chart.series[d.seriesIndex].pointWidth = n;
    //     }
    // }

    var getToolTipContent = function getToolTipContent(_ref22) {
      var config = _ref22.config;
      return function (d) {
        return isBenchmark(d) ? ChartUtils.tooltipContent("".concat(d.measure.name, " (Target)"), d.label) : ReportLinks.BarCharts.tooltipContent(config, d.measure, d.linkParams.displayValue, d.seriesIndex);
      };
    };
    var getLinkParams = function getLinkParams(_ref23) {
      var config = _ref23.config;
      return function (d) {
        return ReportLinks.hasLinkConfig(config, d.measure, d.seriesIndex) ? d.linkParams : {};
      };
    };
    var addSeriesDataToChart = function addSeriesDataToChart(chart, d, s) {
      return chart.series[d.seriesIndex].data.push(s);
    };
    var addSeriesToChart = function addSeriesToChart(_ref24) {
      var config = _ref24.config,
        chart = _ref24.chart;
      return function (d) {
        var y = getSeriesYValue(d);
        var name = getSeriesName(d);
        var tooltipContents = getToolTipContent({
          config: config
        })(d);
        var linkParams = getLinkParams({
          config: config
        })(d);
        // setPointSize(chart, d, 20); // note: differentiating benchmark bars by size isn't straightforward
        addSeriesDataToChart(chart, d, {
          y: y,
          name: name,
          tooltipContents: tooltipContents,
          linkParams: linkParams
        });
      };
    };
    var buildSeriesData = function buildSeriesData(_ref25, _ref26) {
      var config = _ref25.config,
        chartConfig = _ref25.chartConfig,
        chart = _ref25.chart;
      var aggregations = _ref26.aggregations,
        values = _ref26.values;
      return chartConfig.data.map(addSeriesData({
        aggregations: aggregations,
        values: values
      })).map(labelSeriesData({
        config: config,
        chartConfig: chartConfig,
        aggregations: aggregations
      })).map(formatSeriesValue).map(configureLinkParams).forEach(addSeriesToChart({
        config: config,
        chart: chart
      }));
    };
    var buildDataDisplay = function buildDataDisplay(env) {
      buildMeasureAxes(env);
      env.data.filter(validData).forEach(fork(function (data) {
        return buildDimensionAxis(env, data);
      }, function (data) {
        return buildSeriesData(env, data);
      }));
    };
    var buildLegacyBenchmarks = function buildLegacyBenchmarks(_ref27) {
      var config = _ref27.config,
        chart = _ref27.chart;
      //
      // set optional benchmark plotLines and min/max soft limits
      //
      var firstPlotLine = null;
      function addBenchmark(benchmark, i, yAxis) {
        // get benchmark value
        var bm_value = ChartUtils.getSimpleValue(benchmark);
        if (bm_value === null) {
          return;
        }

        // use series color if more than one plotLine
        var plotLineColor = 'red';
        if (firstPlotLine) {
          plotLineColor = getColorOption(i);

          // update color of firstPlotLine
          firstPlotLine.color = getColorOption(firstPlotLine.index);
        }

        // configure plotLine
        var plotLine = {
          value: bm_value,
          color: plotLineColor,
          width: 2,
          id: 'benchmark-' + i,
          index: i
        };

        // add plotLine to yAxis
        yAxis.plotLines = yAxis.plotLines || [];
        yAxis.plotLines.push(plotLine);

        // save first plotLine in case we need to change its color
        firstPlotLine = firstPlotLine || plotLine;
      }

      // todo: implement simple report-level benchmarks (config.benchmarks)
      // todo: prefer config.benchmarks
      // check each measure for benchmark, min/max
      // todo: handle config level benchmarks
      _.chain(config.measures).filter(function (m, i) {
        // todo: use index
        return config.hiddenMeasures.indexOf(i) === -1;
      }).forEach(function (m, i) {
        // determine which yAxis to apply to
        var yAxisId = chart.series[i].yAxis; // todo: seriesData[i].config.yAxis
        var yAxis = yAxisId ? chart.yAxis.find(function (a) {
          return a.id === yAxisId;
        }) : chart.yAxis[0];
        addBenchmark(m.benchmark, i, yAxis); // note

        // todo: set soft min/max based on chart range & benchmark(s)

        // set "hard" min/max specified on measures
        ChartUtils.setAxisMin(m.min, yAxis);
        ChartUtils.setAxisMax(m.max, yAxis);
      });
    };
    var setStackingMode = function setStackingMode(_ref28) {
      var config = _ref28.config,
        chartConfig = _ref28.chartConfig,
        chart = _ref28.chart;
      // if present, add bar stacking option.  Can be 'normal', 'percent', or null (default)
      if (config.stacking && config.measures.length > 1) {
        chart.plotOptions.series = Object.assign(chart.plotOptions.series, {
          stacking: config.stacking
        });
        chartConfig.data.forEach(function (d) {
          if (isMeasure(d)) {
            chart.series[d.seriesIndex].stack = 'measure';
          }
          if (isBenchmark(d)) {
            chart.series[d.seriesIndex].stack = 'benchmark';
          }
        });
      }
    };
    var setDataLabelVisibility = function setDataLabelVisibility(_ref29) {
      var config = _ref29.config,
        chart = _ref29.chart;
      if (config.showDataLabels) {
        chart.plotOptions.series = Object.assign(chart.plotOptions.series, {
          dataLabels: {
            enabled: true,
            // Use defined configuration rotation, else 0 degrees
            rotation: config.dataLabelRotation || 0,
            formatter: function formatter() {
              return this.point.name;
            },
            crop: false
          }
        });
      }
    };
    var getHighChartsBuilder = function getHighChartsBuilder(spec) {
      var closure = {
        env: Object.assign({}, spec)
      };
      var builder = {};
      var lift = function lift(f) {
        return function () {
          f(closure.env);
          return builder;
        };
      };
      var mapEnv = function mapEnv(f) {
        return lift(function (env) {
          return closure.env = f(env);
        });
      };
      var bind = function bind(k, f) {
        return lift(function (env) {
          return closure.env[k] = f(env);
        });
      };
      builder.configureLabels = mapEnv(configureLabels);
      builder.buildChartConfig = bind('chartConfig', buildChartConfig);
      builder.initChart = bind('chart', initChart);
      builder.configureSeries = bind('chartConfig.data', configureSeries);
      builder.configureChart = bind('chart', configureChart);
      builder.buildDataDisplay = lift(buildDataDisplay);
      builder.buildLegacyBenchmarks = lift(buildLegacyBenchmarks);
      builder.setStackingMode = lift(setStackingMode);
      builder.setDataLabelVisibility = lift(setDataLabelVisibility);
      builder.getEnv = function () {
        return closure.env;
      };
      builder.getChart = function () {
        return closure.env.chart;
      };
      builder.buildChart = function () {
        return builder.configureLabels().buildChartConfig().configureSeries().initChart().configureChart().buildDataDisplay().buildLegacyBenchmarks().setStackingMode().setDataLabelVisibility();
      };
      return builder;
    };
    return assignMethods({}, [['getHighChartsBuilder', getHighChartsBuilder]]);
  }]);
})();
